/**
 * Logout Hook
 * 
 * React hook for logout functionality.
 * Uses factory pattern to enable dependency injection for package extraction.
 * 
 * Implements fail-open semantics - logout always completes from user perspective.
 */

import { useState, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { useAuth } from '../../../context/AuthContext';
import type { LogoutParams, LogoutResult } from '../types/logout.types';
import { LogoutService } from '../services/logout.service';
import { createMsalAdapter } from '../adapters/MsalAdapter';
import { createConfigAdapter } from '../adapters/ConfigAdapter';
import { createStorageAdapter } from '../adapters/StorageAdapter';
import { createAuthContextAdapter } from '../adapters/AuthContextAdapter';

/**
 * Factory function to create useLogout hook
 * 
 * This enables package extraction - different projects can provide different adapters.
 * 
 * @param service - LogoutService instance with injected adapters
 * @returns useLogout hook
 */
export function createUseLogout(
  service: LogoutService
) {
  return function useLogout() {
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    const logout = useCallback(
      async (params?: LogoutParams): Promise<LogoutResult> => {
        setIsLoggingOut(true);

        try {
          // LogoutService.executeLogout() already handles everything:
          // - Backend logout (best-effort, 5s timeout)
          // - clearAuthStateSilently() (clears React state)
          // - Clear tokens, storage, MSAL cache
          // - MSAL logoutRedirect()
          const result = await service.executeLogout(params);
          return result;
        } catch (err) {
          // Log but don't throw - fail-open semantics
          // LogoutService already cleared state and called logoutRedirect
          console.error('[useLogout] Logout error (non-blocking):', err);
          
          return {
            success: true, // Fail-open: always return success
            error: err as Error,
          };
        } finally {
          setIsLoggingOut(false);
        }
      },
      [service]
    );

    return {
      logout,
      isLoggingOut,
    };
  };
}

/**
 * Healthcare Portal implementation of useLogout
 * 
 * Creates service with Healthcare Portal-specific adapters.
 * Hook is created once at module level, not dynamically.
 */
export const useLogout = (() => {
  // This will be called inside components, so we need to create adapters inside the hook
  return function useLogoutInternal() {
    const { instance } = useMsal();
    const auth = useAuth();

    // Create adapters with current context
    const msalAdapter = createMsalAdapter(instance as any);
    const configAdapter = createConfigAdapter();
    const storageAdapter = createStorageAdapter(instance as any);
    const authAdapter = createAuthContextAdapter(auth);

    // Create service with adapters
    const service = new LogoutService(
      msalAdapter,
      configAdapter,
      storageAdapter,
      authAdapter
    );

    // Use the factory-created hook
    const hook = createUseLogout(service);
    return hook();
  };
})();
